// Common mixin for timeline item editing
import EditCommentDialog from "@/components/EditCommentDialog.vue"

export const timelineEditMixin = {
  data() {
    return {
      editMethods: {
        button: false,      // Traditional buttons
        longPress: false,   // Long press
      },
      longPressTimer: null,
      longPressStartTime: null,
      isLongPressing: false
    }
  },

  methods: {
    /**
     * Returns safe interaction handlers that always provide valid functions
     * @param {Object} item - Timeline element (optional when readOnly)
     * @param {Function} editCallback - Edit function (optional when readOnly)
     * @param {Boolean} readOnly - Whether the component is in read-only mode
     * @returns {Object} - Object with safe event handlers
     */
    getSafeInteractionHandlers(item = null, editCallback = null, readOnly = false) {
      // Return semantic handlers so templates that bind to these properties won't receive `undefined`
      if (readOnly) {
        const noop = () => {}
        return {
          // Semantic handlers used by templates
          onPressStart: noop,
          onPressEnd: noop,
          onPressCancel: noop
        }
      }

      // Use full interaction handlers for editable mode
      return this.createInteractionHandlers(item, editCallback)
    },

    /**
     * Creates handlers for alternative editing methods
     * @param {Object} item - Timeline element
     * @param {Function} editCallback - Edit function
     * @param {Function} deleteCallback - Delete function
     * @returns {Object} - Object with event handlers
     */
    createInteractionHandlers(item, editCallback) {
      const handlers = {
        // Always provide default empty handlers to avoid undefined errors
        onPressStart: () => {},
        onPressEnd: () => {},
        onPressCancel: () => {}
      }

      // Long press handler
      if (this.editMethods.longPress && this.canEditItem(item)) {
        // Unified handlers for both mouse and touch events
        const startHandler = (event) => this.startLongPress(event, item, editCallback)
        const endHandler = () => this.endLongPress()
        const cancelHandler = () => this.cancelLongPress()

        // Semantic event handlers (work for both mouse and touch)
        handlers.onPressStart = startHandler  // mousedown + touchstart
        handlers.onPressEnd = endHandler      // mouseup + touchend
        handlers.onPressCancel = cancelHandler // mouseleave + touchcancel
      }

      return handlers
    },

    /**
     * Starts long press for both mouse and touch events
     */
    startLongPress(event, item, editCallback) {
      // Prevent default behavior and stop propagation
      event.preventDefault()
      event.stopPropagation()

      // Check if already long pressing to avoid multiple timers
      if (this.isLongPressing) {
        return
      }

      this.isLongPressing = true
      this.longPressStartTime = Date.now()
      this.isPressed = true

      this.longPressTimer = setTimeout(() => {
        if (this.isLongPressing) {
          this.handleAlternativeEdit(item, editCallback)
        }
      }, 600)
    },

    /**
     * Ends long press
     */
    endLongPress() {
      this.isPressed = false
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer)
        this.longPressTimer = null
      }
      this.isLongPressing = false
    },

    /**
     * Cancels long press
     */
    cancelLongPress() {
      this.endLongPress()
    },

    /**
     * Handles alternative editing with visual feedback
     */
    async handleAlternativeEdit(item, editCallback) {
      // Add visual feedback
      if (this.$toast) {
        this.$toast.info('Editing element...', { timeout: 1000 })
      }

      if (editCallback) {
        await editCallback()
      } else {
        await this.openEditDialog(item, this.defaultUpdateCallback)
      }
    },

    /**
     * Checks if element can be edited
     */
    canEditItem(item) {
      return item.canEdit !== false && (item.editMethod || !this.readOnly)
    },

    /**
     * Default update callback
     */
    defaultUpdateCallback(item, result) {
      // Emit update event
      this.$emit('item-updated', { item, result })
    },
    /**
     * Opens comment/message edit dialog
     * @param {Object} item - Timeline element to edit
     * @param {Function} updateCallback - Function called after successful edit
     */
    async openEditDialog(item, updateCallback) {
      try {
        // Prepare edit data based on element type
        const editData = this.prepareEditData(item)

        const dialogInstance = await this.$dialog.show(EditCommentDialog, {
          title: editData.title,
          model: editData.model,
          canDelete: item.canDelete || false
        })

        dialogInstance.waitForResult = true
        const result = await dialogInstance.wait()

        if (result === 'delete') {
          // Handle element deletion
          if (updateCallback) {
            await updateCallback(item, null, true) // third parameter means delete
          }
        } else if (result) {
          // Update source data
          this.updateItemData(item, result)

          // Call update callback
          if (updateCallback) {
            await updateCallback(item, result)
          }
        }
      } catch (error) {
        console.error('Error during edit:', error)
        this.$dialog.notify.error("Edit error")
      }
    },

    /**
     * Prepares edit data based on element type
     * @param {Object} item - Timeline element
     * @returns {Object} - Edit data with title and model
     */
    prepareEditData(item) {
      // For MessageItem
      if (item.type === 'message' || item.constructor.name === 'MessageItem') {
        return {
          title: item.title || 'Edit message',
          model: {
            message: item.subtitle || item.message || '',
            comment: item.message || item.details || ''
          }
        }
      }

      // For other types (SimpleItem, etc.)
      if (item.source) {
        return {
          title: item.title || 'Edit details',
          model: {
            message: item.source.message || item.title || '',
            comment: item.source.comment || item.subtitle || ''
          }
        }
      }

      // Fallback
      return {
        title: 'Edit',
        model: {
          message: item.title || '',
          comment: item.subtitle || item.message || ''
        }
      }
    },

    /**
     * Updates element data after editing
     * @param {Object} item - Timeline element
     * @param {Object} editedData - Edited data from dialog
     */
    updateItemData(item, editedData) {
      // For MessageItem
      if (item.type === 'message' || item.constructor.name === 'MessageItem') {
        if (editedData.message !== undefined) {
          item.subtitle = editedData.message
        }
        if (editedData.comment !== undefined) {
          item.message = editedData.comment
        }
      }

      // Update source data if exists
      if (item.source) {
        if (editedData.message !== undefined) {
          item.source.message = editedData.message
        }
        if (editedData.comment !== undefined) {
          item.source.comment = editedData.comment
        }
      }
    }
  }
}
